import { NetworkStatus, OnSubscriptionDataOptions } from '@apollo/client'
import { useDocumentVisibility, useInterval, usePrevious, useScroll } from 'ahooks'
import { message } from 'antd'
import { getOperationName } from 'apollo-link'
import { clone, isNumber, last, setWith, takeWhile } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  GetFeaturedPostsDocument,
  GetPostListDocument,
  GetTimelinePostsDocument,
  GetTimelinePostsQuery,
  GetTopFeaturedPostsDocument,
  GetTopPostDocument,
  Group,
  PaginationInput,
  PostAddedDocument,
  PostAddedSubscription,
  PostDeletedDocument,
  PostDeletedSubscription,
  PostType,
  RequestedPostFragment,
  SectionType,
  useFeaturedCommentDisplayMutation,
  useFeaturedCommentHideMutation,
  useFeaturedPostTopMutation,
  useFeaturedPostUnTopMutation,
  useGetTimelineNewestPageInfoLazyQuery,
  useGetTimelinePostsQuery,
  useGetViewerQuery,
  usePostArchiveMutation,
  usePostFavorToggleMutation,
  usePostFeatureMutation,
  usePostTopMutation,
  usePostUnArchiveMutation,
  usePostUnFeatureMutation,
  usePostUnTopMutation,
  useSectionPostTopMutation,
  useSectionPostUnTopMutation,
} from '@/generated/graphql'

export type PostsSortBy = 'createdAtDesc' | 'createdAtAsc' | 'likedDesc'

export const useArchivePost = (postId: number) => {
  const [archivePost] = usePostArchiveMutation({
    variables: {
      postId,
    },
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
  })

  const [unArchivePost] = usePostUnArchiveMutation({
    variables: {
      postId,
    },
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!],
  })
  return [archivePost, unArchivePost]
}

export const useFeaturePost = (postId: number) => {
  const refetchQueries = [getOperationName(GetFeaturedPostsDocument)!, getOperationName(GetTopFeaturedPostsDocument)!]
  const [featurePost] = usePostFeatureMutation({
    variables: {
      postId,
    },
    refetchQueries,
  })

  const [unFeaturePost] = usePostUnFeatureMutation({
    variables: {
      postId,
    },
    refetchQueries,
  })
  return [featurePost, unFeaturePost]
}

export const useTopPost = (post: RequestedPostFragment, isGroup: boolean, isSection: boolean) => {
  const variables = {
    postId: post.postId,
    ...((isGroup && { groupId: post.groupId }) as any),
    ...((isSection && { sectionId: post.sectionId }) as any),
  }

  const postTopMutation = isGroup ? usePostTopMutation : useSectionPostTopMutation
  const postUnTopMutation = isGroup ? usePostUnTopMutation : useSectionPostUnTopMutation

  const refetchQueries = {
    refetchQueries: [getOperationName(GetTimelinePostsDocument)!, getOperationName(GetTopPostDocument)!],
  }

  const [topPost] = postTopMutation({
    variables,
    ...refetchQueries,
  })

  const [untopPost] = postUnTopMutation({
    variables,
    ...refetchQueries,
  })

  return [topPost, untopPost]
}

export const useTopFeatured = (postId: number) => {
  const refetchQueries = [
    getOperationName(GetFeaturedPostsDocument)!,
    getOperationName(GetTopFeaturedPostsDocument)!,
    getOperationName(GetTimelinePostsDocument)!,
    getOperationName(GetTopPostDocument)!,
  ]

  const [topFeatured] = useFeaturedPostTopMutation({
    variables: {
      postId,
    },
    refetchQueries,
  })

  const [untopFeatured] = useFeaturedPostUnTopMutation({
    variables: {
      postId,
    },
    refetchQueries,
  })

  return [topFeatured, untopFeatured]
}

export const useFeaturedCommentDisplayer = (postId: number) => {
  const [displayComment] = useFeaturedCommentDisplayMutation({
    variables: {
      postId,
    },
    refetchQueries: [getOperationName(GetFeaturedPostsDocument)!, getOperationName(GetTopFeaturedPostsDocument)!],
  })

  const [hideComment] = useFeaturedCommentHideMutation({
    variables: {
      postId,
    },
    refetchQueries: [getOperationName(GetFeaturedPostsDocument)!, getOperationName(GetTopFeaturedPostsDocument)!],
  })

  return [displayComment, hideComment]
}

export const useLastUnreadPost = (posts: RequestedPostFragment[]) => {
  const [lastUnreadPost, setLastUnreadPost] = React.useState<RequestedPostFragment | null>(null)

  React.useEffect(() => {
    const p = last(
      takeWhile(
        posts.filter((p) => !p.isViewerCreated),
        (p) => !p.isRead
      )
    )
    if (p) {
      setLastUnreadPost(p)
    }
  }, [posts])

  return lastUnreadPost
}

export const usePostFavorToggle = (postId: number) => {
  const [t] = useTranslation()
  const [toggleFavorPost] = usePostFavorToggleMutation({
    variables: { postId },
    refetchQueries: [getOperationName(GetPostListDocument)!],
    onCompleted(data) {
      if (data?.postFavorToggle.isFavored) {
        message.success(t('post.favoriteSuccess'))
      }
    },
  })
  return [toggleFavorPost]
}

const getPostsParent = (prev: GetTimelinePostsQuery) => {
  const entity = prev.viewer || prev.user || prev.group || prev.section
  const entityPath: 'viewer' | 'group' | 'user' | 'section' = prev.viewer
    ? 'viewer'
    : prev.group
      ? 'group'
      : prev.section
        ? 'section'
        : 'user'
  return { entity, entityPath }
}

const addNewPostToCache = (prev: GetTimelinePostsQuery, newPost: RequestedPostFragment) => {
  const { entity, entityPath } = getPostsParent(prev)

  if (
    (entityPath === 'viewer' && entity?.['allPosts']) ||
    !entity?.posts ||
    entity.posts.edges.find((e) => e.node.postId === newPost.postId)
  ) {
    return prev
  }

  return setWith(clone(prev), [entityPath, 'posts', 'edges'], [...entity!.posts!.edges, { node: newPost }], clone)
}

const deletePostFromCache = (prev: GetTimelinePostsQuery, postId: number) => {
  const { entity, entityPath } = getPostsParent(prev)
  // 只修改包含删除的 post 或分享过删除 post 的 cache
  if (
    (entityPath === 'viewer' && entity?.['allPosts']) ||
    !entity?.posts ||
    entity.posts.edges.every(
      (e) => e.node.postId !== postId && e.node.originalPost && e.node.originalPost.postId !== postId
    )
  ) {
    return prev
  }

  // 将包含删除 post 的分享的 originalPost 置为 null
  const indexes: number[] = []
  prev[entityPath]!.posts!.edges.forEach(({ node }, index) => {
    if (node.originalPost && node.originalPost.postId === postId) {
      indexes.push(index)
    }
  })
  let curr = prev
  for (const index of indexes) {
    curr = setWith(clone(curr), [entityPath, 'posts', 'edges', index, 'node', 'originalPost'], null, clone)
  }

  // 移除删除的 post
  const edges = curr[entityPath]!.posts!.edges
  return setWith(
    clone(curr),
    [entityPath, 'posts', 'edges'],
    edges.filter(({ node }) => node.postId !== postId),
    clone
  )
}

export const useGetTimelinePosts = ({
  isAllPosts,
  group,
  userId,
  sortBy = 'createdAtDesc',
  postTypes,
  sectionId,
  departmentId,
  startTs,
  endTs,
  useRecommendation,
  skip = false,
}: {
  isAllPosts?: boolean
  group?: Group
  userId?: number
  sortBy?: PostsSortBy
  postTypes?: PostType[]
  sectionId?: SectionType
  departmentId?: number
  startTs?: Date
  endTs?: Date
  useRecommendation?: boolean
  skip?: boolean
}) => {
  const { data: me } = useGetViewerQuery()
  const myUserId = me!.viewer.userId

  const paginationCount = 10

  const pagination = (fetchMore: boolean): PaginationInput => {
    switch (sortBy) {
      case 'createdAtDesc':
        return {
          last: paginationCount,
          before: fetchMore ? pageInfo.startCursor : undefined,
        }
      case 'createdAtAsc':
        return {
          first: paginationCount,
          after: fetchMore ? pageInfo.endCursor : undefined,
        }
      case 'likedDesc':
        return {
          last: paginationCount,
          sortBy: 'like',
          before: fetchMore ? pageInfo.startCursor : undefined,
        }
    }
  }

  const { data, error, fetchMore, subscribeToMore, refetch, networkStatus } = useGetTimelinePostsQuery({
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first', // 不然 fetchMore 翻页会进入异常
    notifyOnNetworkStatusChange: true, // 用于显示加载更多的 loading
    variables: {
      withDepartmentJournal: !!departmentId,
      withViewerFollowing: (!!isAllPosts || (!userId && !group && !sectionId)) && !isNumber(departmentId),
      withUserCreated: !!userId,
      withGroup: !!group,
      withAllPosts: !!isAllPosts,
      withSection: !!sectionId,
      userId: userId || 0,
      sectionId: sectionId || SectionType.Null,
      groupId: group ? group.groupId : 0,
      departmentId: departmentId ?? 0,
      pagination: pagination(false),
      filter: {
        postTypes: postTypes,
        ...(startTs && endTs && { startTs }),
        ...(startTs && endTs && { endTs }),
      },
      useRecommendation: !userId && !group && !isAllPosts ? useRecommendation : false,
    },
  })

  React.useEffect(() => {
    ;(subscribeToMore as any)({
      document: PostAddedDocument,
      variables: {
        userId: userId || myUserId,
        groupId: group?.groupId,
      },
      updateQuery: (
        prev: GetTimelinePostsQuery,
        { subscriptionData }: OnSubscriptionDataOptions<PostAddedSubscription>
      ) => addNewPostToCache(prev, subscriptionData.data!.postAdded),
    })
    ;(subscribeToMore as any)({
      document: PostDeletedDocument,
      updateQuery: (
        prev: GetTimelinePostsQuery,
        { subscriptionData }: OnSubscriptionDataOptions<PostDeletedSubscription>
      ) => deletePostFromCache(prev, subscriptionData.data!.postDeleted),
    })
  }, [])

  const onLoadMore = () => {
    fetchMore({
      variables: {
        pagination: pagination(true),
      },
    })
  }

  const entity = data ? data.viewer || data.user || data.group || data.department || data.section : null
  const rootNode = entity ? entity['allPosts'] || entity.posts : null
  const edges = rootNode ? rootNode.edges : []
  const pageInfo = rootNode
    ? rootNode.pageInfo
    : {
        startCursor: null,
        endCursor: null,
        hasPreviousPage: false,
        hasNextPage: false,
      }
  const posts = ((): any[] => {
    switch (sortBy) {
      case 'createdAtDesc':
        return edges.slice().reverse()
      case 'createdAtAsc':
        return edges
      case 'likedDesc':
        return edges.slice().reverse()
    }
  })().map(({ node }: any) => node)
  return {
    posts,
    pageInfo,
    loading: networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables,
    loadingMore: networkStatus === NetworkStatus.fetchMore,
    error,
    refetch,
    onLoadMore,
  }
}

/*
 * 通过条件拉取最新的帖子
 */
export const useFetchNewPosts = ({ skip, fetch }: { skip: boolean; fetch: (newEndCursor?: string | null) => void }) => {
  const currentVisibility = useDocumentVisibility()
  const previousVisibility = usePrevious(currentVisibility)
  const scroll = useScroll(document)
  const prevTop = usePrevious(scroll.top)!
  const topBoundary = 300

  const [refetch, { data }] = useGetTimelineNewestPageInfoLazyQuery({
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    skip,
  })

  const endCursor = data?.section.posts.pageInfo.endCursor

  // 停留在界面顶部
  useInterval(() => {
    if (skip) {
      return
    }
    if (scroll.top < topBoundary && currentVisibility === 'visible') {
      refetch()
    }
  }, 60000)

  // 从别的 tab 切换来
  React.useEffect(() => {
    if (skip) {
      return
    }
    if (previousVisibility === 'hidden' && currentVisibility === 'visible') {
      refetch()
    }
  }, [previousVisibility, currentVisibility])

  // 滚动到顶部
  React.useEffect(() => {
    if (skip) {
      return
    }
    if (prevTop > scroll.top && scroll.top === 0) {
      refetch()
    }
  }, [scroll.top, prevTop])

  // 如果有新的内容，触发完整的 Timeline 拉取
  // 只在页面顶部拉取，如果在页面下面拉取的话，影响阅读
  React.useEffect(() => {
    if (skip) {
      return
    }
    if (scroll.top < topBoundary && endCursor) {
      fetch(endCursor)
    }
  }, [endCursor])
}

/**
 * 点击左侧栏动态消息 Menu 刷新
 */
export const useLocationKeyChange = (cb: Function) => {
  const { key } = useLocation()
  const previousKey = usePrevious(key)
  React.useEffect(() => {
    if (previousKey && previousKey !== key) {
      cb()
      window.scrollTo({ top: 0 })
    }
  }, [key, previousKey])
}
